import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, getDocs, query, where, collectionGroup } from 'firebase/firestore';
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { BookOpen, Users, ArrowRight, Search } from 'lucide-react';

export default function GuestStorefront() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadAuthors() {
      try {
        // Load all authors
        const authorsSnap = await getDocs(collection(db, 'authors'));
        const authorList = [];
        for (const authorDoc of authorsSnap.docs) {
          const data = authorDoc.data();
          // Check if author has at least one published/active book
          const booksSnap = await getDocs(collection(db, 'authors', authorDoc.id, 'books'));
          const hasActiveBook = booksSnap.docs.some(b => {
            const bd = b.data();
            return bd.status !== 'archived' && bd.status !== 'upcoming';
          });
          if (hasActiveBook) {
            // Load branding
            let branding = {};
            try {
              const brandingSnap = await getDocs(collection(db, 'authors', authorDoc.id, 'settings'));
              brandingSnap.forEach(s => {
                if (s.id === 'branding') branding = s.data();
              });
            } catch { /* skip */ }
            authorList.push({
              id: authorDoc.id,
              name: branding.displayName || data.penName || data.name || 'Author',
              bio: branding.bio || '',
              logo: branding.logo || '',
              banner: branding.banner || '',
              bookCount: booksSnap.size,
            });
          }
        }
        setAuthors(authorList);
      } catch (err) {
        console.error('Failed to load authors:', err);
      } finally {
        setLoading(false);
      }
    }
    loadAuthors();
  }, []);

  const filtered = searchQuery
    ? authors.filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : authors;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/30">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-slate-500 to-teal-600">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Narratoria Store</h1>
                <p className="text-xs text-slate-500">Browse books from our authors</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { window.location.href = '/reader?signup=true'; }}
              className="gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50"
            >
              Reader Portal <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {/* CTA Banner */}
        <div className="mb-8 rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 to-slate-50 p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Want early access to books?</h2>
              <p className="mt-1 text-sm text-slate-600">
                Register on the reader portal to join author ARC teams, get advance copies, earn rewards, and access exclusive content.
              </p>
            </div>
            <Button
              onClick={() => { window.location.href = '/reader?signup=true'; }}
              className="shrink-0 gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
            >
              <Users className="h-4 w-4" /> Register Now
            </Button>
          </div>
        </div>

        {/* Search */}
        {authors.length > 3 && (
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search authors..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
            />
          </div>
        )}

        {/* Authors Grid */}
        <h2 className="mb-4 text-xl font-bold text-slate-900">Authors</h2>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-100 border-t-teal-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center">
            <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-slate-500">{searchQuery ? 'No authors match your search.' : 'No authors with published books yet.'}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(author => (
              <button
                key={author.id}
                onClick={() => { window.location.href = `/store/${author.id}`; }}
                className="group text-left"
              >
                <Card className="overflow-hidden border-slate-200 transition-all hover:border-teal-300 hover:shadow-md">
                  {/* Banner or gradient */}
                  <div className="h-24 bg-gradient-to-br from-slate-200 to-teal-100">
                    {author.banner && (
                      <img src={author.banner} alt="" className="h-full w-full object-cover" />
                    )}
                  </div>
                  <div className="relative px-5 pb-5 pt-8">
                    {/* Avatar */}
                    <div className="absolute -top-8 left-5">
                      {author.logo ? (
                        <img src={author.logo} alt={author.name} className="h-16 w-16 rounded-full border-4 border-white object-cover shadow-sm" />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-slate-500 to-teal-600 text-xl font-bold text-white shadow-sm">
                          {author.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-teal-700 transition-colors">{author.name}</h3>
                    {author.bio && (
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{author.bio}</p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600 border-0">
                        {author.bookCount} {author.bookCount === 1 ? 'book' : 'books'}
                      </Badge>
                      <span className="text-xs text-teal-600 font-medium group-hover:underline">View store →</span>
                    </div>
                  </div>
                </Card>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-slate-100 bg-white py-6">
        <div className="mx-auto max-w-5xl px-4 text-center text-xs text-slate-400 sm:px-6">
          <p>Narratoria Ltd</p>
          <p className="mt-1">
            Need help? <a href="mailto:julieanna.mac.author@gmail.com" className="text-teal-600 hover:underline">julieanna.mac.author@gmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
